import {
	Button,
	Checkbox,
	Code,
	Flex,
	Group,
	Input,
	Loader,
	Table,
	Tooltip,
} from '@mantine/core';
import {
	IconCurrencyPound,
	IconList,
	IconPercentage,
	IconSearch,
} from '@tabler/icons-react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { KeyedRow } from '../../components/KeyedRow';
import { PageHeader } from '../../components/PageHeader';
import { ProductFilterButton } from '../../components/ProductFilterButton';
import { SmallModal } from '../../components/SmallModal';
import { Title } from '../../components/Title';
import { FormBranchSelector } from '../../components/form/FormBranchSelector';
import { useModal } from '../../hooks/useModal';
import { usePagination } from '../../hooks/usePagination';
import { useProductFilterForm } from '../../hooks/useProductFilterForm';
import { useRowSelection } from '../../hooks/useRowSelection';
import { whiteIconButtonStyle } from '../../theme/styling/buttonStyling';
import { defaultIconStyle } from '../../theme/styling/iconStyling';
import { query } from '../../util/api/query';
import { BulkUpdateProductModal } from '../products/components/BulkUpdateProductModal';
import { BulkUpdateProductPriceModal } from './components/BulkUpdateProductPriceModal';
import { PercentagePriceChangeModal } from './components/PercentagePriceChangeModal';
import { PriceChangeRow } from './components/PriceChangeRow';
import type {
	BranchSkuidPairDto,
	ProductPriceDto,
} from './types/ProductPriceDto';

export function PriceChange() {
	const { organisation } = useParams();
	const pagination = usePagination();

	const { form, urlParams } = useProductFilterForm(pagination);

	const productsQuery = query<ProductPriceDto[]>(
		`${organisation}/product-price/all?${urlParams.valueWithPagination}`,
	);

	const productsCountQuery = query<number>(
		`${organisation}/product-price/count?${urlParams.value}`,
	);

	const selection = useRowSelection<BranchSkuidPairDto>(
		productsCountQuery.data?.value ?? 0,
		(a, b) => a.skuid === b.skuid && a.branchId === b.branchId,
	);

	const bulkEditModal = useModal();
	const percentageChangeModal = useModal();
	const bulkProductEditModal = useModal();
	const bulkProductWarningModal = useModal();

	useEffect(() => {
		selection.deselectAll();
	}, [form.values]);

	return (
		<>
			<BulkUpdateProductPriceModal
				isOpened={bulkEditModal.isOpen}
				close={bulkEditModal.close}
				onSuccess={() => {
					productsQuery.remove();
					productsQuery.refetch();
				}}
				rowSelection={selection}
				urlParams={urlParams.value}
			/>
			<PercentagePriceChangeModal
				isOpened={percentageChangeModal.isOpen}
				close={percentageChangeModal.close}
				onSuccess={() => {
					productsQuery.remove();
					productsQuery.refetch();
				}}
				rowSelection={selection}
				urlParams={urlParams.value}
			/>
			<BulkUpdateProductModal
				isOpened={bulkProductEditModal.isOpen}
				close={bulkProductEditModal.close}
				onSuccess={() => {
					productsQuery.remove();
					productsQuery.refetch();
				}}
				rowSelection={selection}
				getSkuid={(key) => key.skuid}
				urlParams={urlParams.value}
			/>
			<SmallModal
				isOpen={bulkProductWarningModal.isOpen}
				close={bulkProductWarningModal.close}
				onComplete={() => {
					bulkProductWarningModal.close();
					bulkProductEditModal.open();
				}}
				title={'Shared Product Attributes'}
				body={
					'Some attributes of a product are shared across branches. ' +
					'Updating the Group, Department, Supplier or VAT Rate will ' +
					'update the product across all branches.'
				}
			/>
			<PageHeader title={'Prices'} hideFiltering>
				<Group>
					{productsQuery.isLoading && (
						<Flex align={'center'}>
							<Code bg={'transparent'} pr={10}>
								This may take a couple of seconds{' '}
							</Code>
							<Loader size={10} color="medepos.1" />
						</Flex>
					)}
					<form>
						<Group justify={'flex-end'}>
							<Tooltip label={'Search SKUID or description...'}>
								<Input
									variant={'borderless-filled'}
									placeholder={'Search SKUID or description...'}
									leftSection={<IconSearch {...defaultIconStyle} />}
									w={'20vw'}
									key={form.key('searchQuery')}
									{...form.getInputProps('searchQuery')}
								/>
							</Tooltip>
							<FormBranchSelector
								form={form}
								property={'branchId'}
								placeHolder={'Select branch...'}
								variant={'borderless-filled'}
								forFilter={true}
								hideArea={true}
							/>
							<ProductFilterButton form={form} />
							<Tooltip label={'Bulk edit prices'}>
								<Button
									{...whiteIconButtonStyle}
									onClick={bulkEditModal.open}
									disabled={selection.noneSelected}
								>
									<IconCurrencyPound {...defaultIconStyle} />
								</Button>
							</Tooltip>
							<Tooltip label={'Percentage price change'}>
								<Button
									{...whiteIconButtonStyle}
									onClick={percentageChangeModal.open}
									disabled={selection.noneSelected}
								>
									<IconPercentage {...defaultIconStyle} />
								</Button>
							</Tooltip>
							<Tooltip label={'Bulk edit product information'}>
								<Button
									{...whiteIconButtonStyle}
									onClick={bulkProductWarningModal.open}
									disabled={selection.noneSelected}
								>
									<IconList {...defaultIconStyle} />
								</Button>
							</Tooltip>
						</Group>
					</form>
				</Group>
			</PageHeader>
			<DataTable
				pagination={pagination}
				totalItems={productsCountQuery.data?.value}
				isRowsLoading={productsQuery.isLoading}
				isPaginationLoading={productsCountQuery.isLoading}
				rows={pagination.value.pageSize}
			>
				<Table.Thead>
					<KeyedRow>
						<Table.Th>
							<Checkbox
								color={'medepos.1'}
								checked={selection.allSelected}
								indeterminate={selection.someSelected}
								onChange={selection.toggleAll}
							/>
						</Table.Th>
						<Table.Th>
							<Title label={'SKUID'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Description'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Min. Stock'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Max. Stock'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Branch'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Current Price'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Stock Level'} />
						</Table.Th>
						<Table.Th />
					</KeyedRow>
				</Table.Thead>
				<Table.Tbody>
					{productsQuery.data?.value.map((product) => (
						<PriceChangeRow
							key={product.skuid}
							originalProductPrice={product}
							isSelected={selection.isSelected({
								skuid: product.skuid,
								branchId: product.branchId,
							})}
							onToggle={() =>
								selection.toggleRow({
									skuid: product.skuid,
									branchId: product.branchId,
								})
							}
						/>
					))}
				</Table.Tbody>
			</DataTable>
		</>
	);
}
